import { API_BASE } from './constants.js'
import { publishAblyEvent } from '../hooks/useAblyChannel.js'

/**
 * Client for the shared meta store (comments, weekly summaries, reactions,
 * DMs, presence, action items, etc.).
 *
 * Endpoints (same Cloudflare Worker as /goals):
 *   GET  /meta  — returns the meta object
 *   POST /meta  — saves the full meta object
 *
 * Meta object shape (from q2-goals.html line 2846–2858):
 *   {
 *     fieldEdits:       {},  // "p0-g1-current" → { by, at }
 *     comments:         [],  // threads + replies
 *     weeklySummary:    {},  // "p0-g1" → { tldr, wins, flags, savedAt, savedBy }
 *     execSummary:      null,
 *     aiPreferences:    [],
 *     hsComments:       [],  // owned by hubspot-dashboard, preserved on save
 *     reactions:        {},  // "p0-g1" → { emoji: [user1, user2] }
 *     weeklyHistory:    {},  // "2026-04-14" → snapshot
 *     lastSnapshotWeek: null,
 *     actionItems:      {},  // "p0-g1" → [{ id, task, owner, dueDate, ... }]
 *     dms:              {},  // "Emily,Ricky" → { participants, messages }
 *     presence:         {},  // "Ricky" → { at, field }
 *   }
 *
 * Preservation rule: always read existing meta from server before saving,
 * merge your changes, then POST the full merged object. Otherwise you risk
 * clobbering fields owned by other surfaces (e.g. hsComments from the
 * HubSpot dashboard).
 */

export const DEFAULT_META = {
  fieldEdits: {},
  comments: [],
  weeklySummary: {},
  execSummary: null,
  aiPreferences: [],
  hsComments: [],
  reactions: {},
  weeklyHistory: {},
  lastSnapshotWeek: null,
  actionItems: {},
  dms: {},
  presence: {},
}

export async function fetchMetaData() {
  try {
    const res = await fetch(API_BASE + '/meta')
    if (res.status === 204) return { ...DEFAULT_META }
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    // Merge with defaults so undefined keys don't break consumers
    return { ...DEFAULT_META, ...data }
  } catch (err) {
    console.warn('fetchMetaData: worker unreachable, using defaults', err)
    return { ...DEFAULT_META }
  }
}

export async function saveMetaData(meta) {
  const res = await fetch(API_BASE + '/meta', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(meta),
  })
  if (!res.ok) throw new Error(`Save failed: HTTP ${res.status}`)
  publishAblyEvent('meta-changed').catch(() => {})
  return meta
}

/**
 * Partial update: only update presence[userName] server-side, leave the
 * rest of meta untouched. Used by the heartbeat so frequent 20-second
 * writes can NEVER accidentally clobber a concurrent delete, edit, or
 * comment on another field.
 *
 * Safe to call every 20 seconds — the worker does the merge, not us.
 */
export async function savePresenceHeartbeat({ userName, at, pillar }) {
  if (!userName) return
  const res = await fetch(API_BASE + '/meta/presence', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userName, at, pillar }),
  })
  if (!res.ok) throw new Error(`Presence save failed: HTTP ${res.status}`)
}

/**
 * Archive a week's state (weeklySummary + execSummary + actionItems) into
 * meta.weeklyHistory[weekKey]. Idempotent on the server — won't overwrite
 * an existing snapshot for the same week.
 */
export async function createWeeklySnapshot(weekKey) {
  const res = await fetch(API_BASE + '/meta/snapshot', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ weekKey }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Weekly snapshot failed: HTTP ${res.status} ${msg}`)
  }
  publishAblyEvent('meta-changed').catch(() => {})
  return res.json()
}

/**
 * Save (or clear) the executive summary slot — server-side partial update
 * so concurrent unrelated writers can't clobber it.
 *   payload = { tldr, flagsSummary, generatedAt, generatedBy, edited? }
 *   payload = { clear: true }                       // wipes the slot
 */
export async function saveExecSummary(payload) {
  const res = await fetch(API_BASE + '/meta/execSummary', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {}),
  })
  if (!res.ok) throw new Error(`Exec summary save failed: HTTP ${res.status}`)
  publishAblyEvent('meta-changed').catch(() => {})
  const data = await res.json()
  return data.execSummary
}

/**
 * Add or remove an AI writing preference — server-side partial update.
 *   op = 'create' + pref = { id, text, addedAt, addedBy }
 *   op = 'remove' + id
 * Returns the full post-mutation preferences list.
 */
export async function mutateAiPreferences({ op, pref, id }) {
  const res = await fetch(API_BASE + '/meta/aiPreferences', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ op, pref, id }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`AI prefs ${op} failed: HTTP ${res.status} ${msg}`)
  }
  publishAblyEvent('meta-changed').catch(() => {})
  const data = await res.json()
  return data.aiPreferences
}

/**
 * Ask the worker to call Claude and generate a fresh exec summary.
 * Inputs:
 *   weeklySummaries  — primary: per-goal {goalName, tldr, wins, flags}
 *   subgoalUpdates   — secondary: per-subgoal {pillar, goalName, subgoalName,
 *                                              current, target, unit, update}
 *   aiPreferences    — writing-style rules
 * Returns { tldr, winsSummary, flagsSummary }.
 *
 * Does NOT persist the result — caller decides whether to save via
 * saveExecSummary() after reviewing.
 */
export async function generateExecSummary({
  weeklySummaries,
  subgoalUpdates,
  openActionItems,
  aiPreferences,
}) {
  const res = await fetch(API_BASE + '/ai-summary', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      weeklySummaries,
      subgoalUpdates,
      openActionItems,
      aiPreferences,
    }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`AI generation failed: HTTP ${res.status} ${msg}`)
  }
  return res.json()
}

/**
 * Partial update for a single goal's action-items list. Operations are
 * applied server-side so concurrent unrelated writes (weekly summary
 * edits, comments on other goals) CANNOT resurrect a deleted item.
 *
 * The worker returns the full post-mutation list so we can reconcile
 * the cache with the authoritative server state.
 */
/**
 * Toggle an emoji reaction for a given label (goal name or sub-goal
 * name). Server-side partial update — other meta fields untouched.
 * Returns the post-mutation reactions map for this label:
 *   { '👍': ['Ricky', 'Zoe'], '🎉': ['Katie'] }
 */
export async function toggleReaction({ label, emoji, user }) {
  const res = await fetch(API_BASE + '/meta/reactions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ op: 'toggle', label, emoji, user }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Reaction toggle failed: HTTP ${res.status} ${msg}`)
  }
  publishAblyEvent('meta-changed').catch(() => {})
  const data = await res.json()
  return data.reactions || {}
}

export async function mutateActionItems({ op, goalKey, item, itemId, patch }) {
  const res = await fetch(API_BASE + '/meta/actionItems', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ op, goalKey, item, itemId, patch }),
  })
  if (!res.ok) {
    const msg = await res.text().catch(() => '')
    throw new Error(`Action item ${op} failed: HTTP ${res.status} ${msg}`)
  }
  const data = await res.json()
  // Signal other clients to refetch. `meta-changed` matches the existing
  // channel name used by saveMetaData.
  publishAblyEvent('meta-changed').catch(() => {})
  return data.items
}
